import type { Criteria } from '@nbx/core/http';

import type { Rental, RentalFaq, RentalType } from '@interfaces/rental.interface';
import type { PagedResponse } from '@interfaces/response.interface';

import { mockDb } from '../mock-db';

const FAQ_QUESTION_MAX_LENGTH = 500;
const FAQ_ANSWER_MAX_LENGTH = 10000;
const LOCATION_TEXT_MAX_LENGTH = 5000;

export interface MockCodedRefusal {
  status: number;
  code: string;
  details: Record<string, unknown>;
}

export type RentalContentPatchResult = { rental: Rental } | { refusal: MockCodedRefusal };

const contentLastUpdate = new Map<string, { faqs?: string; locationText?: string }>();

/** When the mock last effectively changed a rental's faqs / locationText, for /sections. */
export function getRentalContentLastUpdateMock(rentalId: string): {
  faqs?: string;
  locationText?: string;
} {
  return contentLastUpdate.get(rentalId) ?? {};
}

export function getRentalByIdMock(id: string): Rental | undefined {
  return mockDb.getRentalById(id);
}

export function searchRentalsMock(
  filters:
    | {
        isDraft?: boolean;
        searchValue?: string;
        type?: string;
        page?: number;
        size?: number;
      }
    | Criteria = {}
): PagedResponse<Rental> {
  return mockDb.searchRentals(filters);
}

export function getAllRentalsMock(page = 0, size = 20): PagedResponse<Rental> {
  return mockDb.getAllRentals(page, size);
}

export function createRentalMock(data?: Partial<Rental>): Rental {
  const base = mockDb.rentals[0];
  return { ...base, id: `rental-${Date.now()}`, ...data };
}

export function updateRentalMock(id: string, data: Partial<Rental>): Rental {
  const existing = mockDb.getRentalById(id);
  const base = existing ?? mockDb.rentals[0];
  return { ...base, id, ...data };
}

/**
 * Applies `PATCH /rentals/{id}` for the public-content fields: an omitted field is kept,
 * `locationText` null/blank clears to null, and `faqs` replaces the whole collection atomically
 * (unknown or duplicated id refused with 422, nothing applied). The result persists into the mock
 * DB so the invalidation reload reads back what was saved.
 */
export function patchRentalContentMock(
  id: string,
  body: Record<string, unknown>
): RentalContentPatchResult {
  const existing = mockDb.getRentalById(id);
  if (!existing) {
    return { refusal: { status: 404, code: 'RENTAL_NOT_FOUND', details: {} } };
  }

  let nextLocationText: string | null | undefined;
  if ('locationText' in body) {
    const raw = body['locationText'];
    const text = typeof raw === 'string' ? raw : '';
    if (text.trim().length === 0) {
      nextLocationText = null;
    } else if (text.length > LOCATION_TEXT_MAX_LENGTH) {
      return {
        refusal: {
          status: 422,
          code: 'RENTAL_LOCATION_TEXT_TOO_LONG',
          details: { field: 'locationText', maxLength: LOCATION_TEXT_MAX_LENGTH },
        },
      };
    } else {
      nextLocationText = text;
    }
  }

  let nextFaqs: RentalFaq[] | undefined;
  if ('faqs' in body) {
    const result = resolveFaqsPatch(existing.faqs ?? [], body['faqs']);
    if ('refusal' in result) {
      return result;
    }
    nextFaqs = result.faqs;
  }

  const stamp = new Date().toISOString();
  const stamps = { ...contentLastUpdate.get(id) };
  if (nextLocationText !== undefined) {
    existing.locationText = nextLocationText;
    stamps.locationText = stamp;
  }
  if (nextFaqs !== undefined) {
    existing.faqs = nextFaqs;
    stamps.faqs = stamp;
  }
  contentLastUpdate.set(id, stamps);

  const { locationText: _lt, faqs: _faqs, ...rest } = body;
  Object.assign(existing, rest);
  return { rental: { ...existing } };
}

function resolveFaqsPatch(
  current: RentalFaq[],
  raw: unknown
): { faqs: RentalFaq[] } | { refusal: MockCodedRefusal } {
  if (!Array.isArray(raw)) {
    return { refusal: { status: 400, code: 'INVALID_REQUEST', details: { field: 'faqs' } } };
  }

  const currentById = new Map(current.map(faq => [faq.id, faq]));
  const seenIds = new Set<string>();
  const next: RentalFaq[] = [];

  for (const [index, item] of raw.entries()) {
    if (typeof item !== 'object' || item === null || Array.isArray(item)) {
      return {
        refusal: {
          status: 422,
          code: 'RENTAL_FAQ_ITEM_INVALID',
          details: { field: `faqs[${index}]`, index },
        },
      };
    }
    const row = item as Record<string, unknown>;
    const idValue = row['id'];
    const previous = typeof idValue === 'string' && idValue ? currentById.get(idValue) : undefined;
    if (typeof idValue === 'string' && idValue) {
      if (!previous) {
        return {
          refusal: {
            status: 422,
            code: 'RENTAL_FAQ_ID_INVALID',
            details: { field: `faqs[${index}].id`, index },
          },
        };
      }
      if (seenIds.has(idValue)) {
        return {
          refusal: {
            status: 422,
            code: 'RENTAL_FAQ_ID_DUPLICATED',
            details: { field: `faqs[${index}].id`, index },
          },
        };
      }
      seenIds.add(idValue);
    }

    const question = typeof row['question'] === 'string' ? row['question'] : '';
    const answer = typeof row['answer'] === 'string' ? row['answer'] : '';
    if (question.trim().length === 0) {
      return {
        refusal: {
          status: 422,
          code: 'FAQ_QUESTION_REQUIRED',
          details: { field: `faqs[${index}].question`, index },
        },
      };
    }
    if (question.length > FAQ_QUESTION_MAX_LENGTH) {
      return {
        refusal: {
          status: 422,
          code: 'FAQ_QUESTION_TOO_LONG',
          details: {
            field: `faqs[${index}].question`,
            index,
            maxLength: FAQ_QUESTION_MAX_LENGTH,
          },
        },
      };
    }
    if (answer.trim().length === 0) {
      return {
        refusal: {
          status: 422,
          code: 'FAQ_ANSWER_REQUIRED',
          details: { field: `faqs[${index}].answer`, index },
        },
      };
    }
    if (answer.length > FAQ_ANSWER_MAX_LENGTH) {
      return {
        refusal: {
          status: 422,
          code: 'FAQ_ANSWER_TOO_LONG',
          details: { field: `faqs[${index}].answer`, index, maxLength: FAQ_ANSWER_MAX_LENGTH },
        },
      };
    }

    next.push({
      id: previous?.id ?? crypto.randomUUID(),
      question,
      answer,
      sortOrder: index,
      enabled: typeof row['enabled'] === 'boolean' ? row['enabled'] : (previous?.enabled ?? true),
    });
  }

  return { faqs: next };
}

export function deleteRentalMock(): { success: boolean } {
  return { success: true };
}

export function getRentalTypesMock(): RentalType[] {
  return mockDb.getRentalTypes();
}

export function createRentalWizardMock(): null {
  return null;
}

export const MOCK_PUBLISH_RENTAL_IDS = {
  INCOMPLETE: 'publish-incomplete-rental',
  NOT_ALLOWED: 'publish-not-allowed-rental',
  FAILED: 'publish-failed-rental',
  UNAVAILABLE: 'publish-unavailable-rental',
} as const;

/** The publish refusal a scenario rental id triggers, or null for the plain 200 success. */
export function getPublishRefusalMock(rentalId: string): MockCodedRefusal | null {
  switch (rentalId) {
    case MOCK_PUBLISH_RENTAL_IDS.INCOMPLETE:
      return {
        status: 422,
        code: 'RENTAL_PUBLISH_INCOMPLETE',
        details: { fields: ['title', 'location.address'] },
      };
    case MOCK_PUBLISH_RENTAL_IDS.NOT_ALLOWED:
      return { status: 422, code: 'RENTAL_PUBLISH_NOT_ALLOWED', details: {} };
    case MOCK_PUBLISH_RENTAL_IDS.FAILED:
      return { status: 500, code: 'RENTAL_PUBLISH_FAILED', details: {} };
    case MOCK_PUBLISH_RENTAL_IDS.UNAVAILABLE:
      return { status: 503, code: 'RENTAL_PUBLISH_TEMPORARILY_UNAVAILABLE', details: {} };
    default:
      return null;
  }
}
